import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Shared link card used in Task Details and Social Media Post Details.
 * Renders a compact cyan pill with the external-link icon and label/url text.
 * Long URLs wrap via break-all/break-words so the layout never breaks.
 */
export const LinkPill = ({ url, label, testId }) => {
  if (!url) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="flex items-center gap-2 text-cyan-600 hover:text-cyan-700 bg-cyan-50 px-4 py-2 rounded-lg hover:bg-cyan-100 transition-colors break-all"
    >
      <ExternalLink size={16} className="flex-shrink-0" />
      <span className="flex-1 min-w-0">
        {label ? <span className="font-medium">{label}: </span> : null}
        <span className="break-words">{url}</span>
      </span>
    </a>
  );
};

/**
 * Renders a list of links with a section heading ("Link" or "Links").
 * Returns null when there are no links to render.
 */
export const LinkPillList = ({ links = [], legacyLink, testIdPrefix = 'link' }) => {
  const hasLinks = Array.isArray(links) && links.length > 0;
  if (!hasLinks && !legacyLink) return null;

  const heading = hasLinks && links.length > 1 ? 'Links' : 'Link';

  return (
    <div>
      <h3 className="text-sm font-semibold text-slate-600 mb-2">{heading}</h3>
      <div className="space-y-2">
        {hasLinks
          ? links.map((item, idx) => (
              <LinkPill
                key={item.url || idx}
                url={item.url}
                label={item.label}
                testId={`${testIdPrefix}-${idx}`}
              />
            ))
          : (
            <LinkPill url={legacyLink} testId={`${testIdPrefix}-legacy`} />
          )}
      </div>
    </div>
  );
};

export default LinkPill;
